import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Checkbox,
  Button,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  Text,
  useColorModeValue,
  SimpleGrid,
  IconButton,
  Image,
  Spinner,
} from '@chakra-ui/react';
import { FaTimes, FaUpload, FaImage } from 'react-icons/fa';
import axios from 'axios';
import { createBazar, updateBazar } from '../../utils/bazarApi';

const CATEGORIE = [
  { value: '', label: 'Seleziona categoria' },
  { value: 'Giochi & tempo libero', label: 'Giochi & tempo libero' },
  { value: 'Libri, fumetti & manuali', label: 'Libri, fumetti & manuali' },
  { value: 'Abbigliamento & accessori', label: 'Abbigliamento & accessori' },
  { value: 'Casa & arredamento', label: 'Casa & arredamento' },
  {
    value: 'Elettrodomestici & elettronica',
    label: 'Elettrodomestici & elettronica',
  },
  { value: 'Attrezzi & fai-da-te', label: 'Attrezzi & fai-da-te' },
  { value: 'Sport & mobilità', label: 'Sport & mobilità' },
  { value: 'Bambini & infanzia', label: 'Bambini & infanzia' },
  { value: 'Varie', label: 'Varie' },
];

const CONDIZIONI = [
  { value: '', label: 'Seleziona condizione' },
  { value: 'nuovo', label: 'Nuovo' },
  { value: 'come nuovo', label: 'Come nuovo' },
  { value: 'ottimo', label: 'Ottimo' },
  { value: 'buono', label: 'Buono' },
  { value: 'discreto', label: 'Discreto' },
];

const STATI = [
  { value: 'disponibile', label: 'Disponibile' },
  { value: 'prenotato', label: 'Prenotato' },
  { value: 'venduto', label: 'Venduto' },
  { value: 'terminato', label: 'Terminato' },
];

const PREFERENZE_CONTATTO = [
  { value: '', label: 'Seleziona preferenza' },
  { value: 'telefono', label: 'Telefono' },
  { value: 'mail', label: 'Email' },
  { value: 'sede', label: 'Sede CIL' },
];

export default function BazarForm({ bazar = null, onSuccess }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    titolo: '',
    descrizione: '',
    prezzo: '',
    categoria: '',
    condizione: '',
    stato: 'disponibile',
    prezzoNegoziabile: false,
    preferenzeContatto: '',
    galleria: [],
  });

  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Se è modifica, carica i dati esistenti
  useEffect(() => {
    if (bazar) {
      const bazarData = bazar.attributes || bazar;
      setFormData({
        titolo: bazarData.titolo || '',
        descrizione: bazarData.descrizione || '',
        prezzo: bazarData.prezzo || '',
        categoria: bazarData.categoria || '',
        condizione: bazarData.condizione || '',
        stato: bazarData.stato || 'disponibile',
        prezzoNegoziabile: bazarData.prezzoNegoziabile || false,
        preferenzeContatto: bazarData.preferenzeContatto || '',
        galleria: bazarData.galleria?.data || bazarData.galleria || [],
      });
    }
  }, [bazar]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Verifica che non ci siano più di 5 immagini totali
    const currentCount = formData.galleria.length;
    const newCount = files.length;
    const totalCount = currentCount + newCount;

    if (totalCount > 5) {
      toast({
        title: 'Troppe immagini',
        description: `Puoi caricare massimo 5 immagini. Hai già ${currentCount} immagine/i e stai cercando di aggiungerne ${newCount}.`,
        status: 'error',
        duration: 5000,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setUploadingImages(true);

    try {
      // Validazione preliminare
      files.forEach((file) => {
        if (!file.type.startsWith('image/')) {
          throw new Error(`Il file ${file.name} non è un'immagine`);
        }
        const MAX_SIZE = 5 * 1024 * 1024; // 5MB
        if (file.size > MAX_SIZE) {
          throw new Error(`Il file ${file.name} è troppo grande (max 5MB)`);
        }
      });

      // Crea FormData per l'upload
      const uploadFormData = new FormData();
      files.forEach((file) => {
        uploadFormData.append('files', file);
      });

      // Carica le immagini tramite l'endpoint dedicato del bazar
      const response = await axios.post(
        '/api/bazar/upload-images',
        uploadFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
        }
      );

      if (!response.data.success || !response.data.images) {
        throw new Error('Errore nel caricamento delle immagini');
      }

      const uploadedImages = response.data.images;

      setFormData((prev) => ({
        ...prev,
        galleria: [...prev.galleria, ...uploadedImages],
      }));

      toast({
        title: 'Immagini caricate',
        description: `${uploadedImages.length} immagine/i caricata/e con successo`,
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Errore nel caricamento',
        description:
          error.response?.data?.message ||
          error.message ||
          'Si è verificato un errore durante il caricamento',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setUploadingImages(false);
      // Reset input file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      galleria: prev.galleria.filter((_, i) => i !== index),
    }));
  };

  const getImageUrl = (img) => {
    // Se è un'immagine già caricata con URL completo
    if (img.url && img.url.startsWith('http')) {
      return img.url;
    }
    // Se è un'immagine da Strapi
    if (img.url) {
      return img.url.startsWith('/')
        ? `${process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337'}${img.url}`
        : img.url;
    }
    // Se ha attributes
    if (img.attributes?.url) {
      return img.attributes.url.startsWith('http')
        ? img.attributes.url
        : `${process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337'}${img.attributes.url}`;
    }
    return null;
  };

  const validateForm = () => {
    if (!formData.titolo.trim()) {
      toast({
        title: 'Campo obbligatorio',
        description: 'Il titolo è obbligatorio',
        status: 'error',
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        titolo: formData.titolo.trim(),
        descrizione: formData.descrizione.trim(),
        prezzo:
          formData.prezzo !== '' &&
          formData.prezzo !== null &&
          formData.prezzo !== undefined
            ? parseFloat(formData.prezzo)
            : null,
        categoria: formData.categoria || null,
        condizione: formData.condizione || null,
        stato: formData.stato,
        prezzoNegoziabile: formData.prezzoNegoziabile,
        preferenzeContatto: formData.preferenzeContatto || null,
        // Galleria: array di ID delle immagini caricate
        galleria: formData.galleria
          .map((img) => {
            // Se è un'immagine appena caricata, usa l'id
            if (img.id) return img.id;
            // Se è un'immagine esistente, usa documentId o id
            if (img.documentId) return img.documentId;
            if (img.attributes?.documentId) return img.attributes.documentId;
            if (img.attributes?.id) return img.attributes.id;
            return null;
          })
          .filter((id) => id !== null),
      };

      if (bazar) {
        // Modifica
        const documentId = bazar.documentId || bazar.id;
        await updateBazar(documentId, submitData);
        toast({
          title: 'Oggetto aggiornato',
          description: "L'oggetto è stato aggiornato con successo",
          status: 'success',
          duration: 3000,
        });
      } else {
        // Creazione
        await createBazar(submitData);
        toast({
          title: 'Oggetto creato',
          description: "L'oggetto è stato creato con successo",
          status: 'success',
          duration: 3000,
        });
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/bazar');
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: error.message || 'Si è verificato un errore',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      boxShadow="lg"
      border="1px solid"
      borderColor={borderColor}
      p={6}
    >
      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          {/* Titolo */}
          <FormControl isRequired>
            <FormLabel>Titolo</FormLabel>
            <Input
              value={formData.titolo}
              onChange={(e) => handleInputChange('titolo', e.target.value)}
              placeholder="Es: Gioco da tavolo Catan"
              size="md"
            />
          </FormControl>

          {/* Descrizione */}
          <FormControl>
            <FormLabel>Descrizione</FormLabel>
            <Textarea
              value={formData.descrizione}
              onChange={(e) => handleInputChange('descrizione', e.target.value)}
              placeholder="Descrivi l'oggetto..."
              rows={6}
              size="md"
            />
          </FormControl>

          {/* Prezzo e categoria in grid */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {/* Prezzo */}
            <FormControl>
              <FormLabel>Prezzo (€)</FormLabel>
              <NumberInput
                value={formData.prezzo}
                onChange={(value) => handleInputChange('prezzo', value)}
                min={0}
                precision={2}
                size="md"
              >
                <NumberInputField placeholder="0.00" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            {/* Categoria */}
            <FormControl>
              <FormLabel>Categoria</FormLabel>
              <Select
                value={formData.categoria}
                onChange={(e) => handleInputChange('categoria', e.target.value)}
                size="md"
              >
                {CATEGORIE.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>

          {/* Condizione e stato in grid */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {/* Condizione */}
            <FormControl>
              <FormLabel>Condizione</FormLabel>
              <Select
                value={formData.condizione}
                onChange={(e) =>
                  handleInputChange('condizione', e.target.value)
                }
                size="md"
              >
                {CONDIZIONI.map((cond) => (
                  <option key={cond.value} value={cond.value}>
                    {cond.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Stato */}
            <FormControl>
              <FormLabel>Stato</FormLabel>
              <Select
                value={formData.stato}
                onChange={(e) => handleInputChange('stato', e.target.value)}
                size="md"
              >
                {STATI.map((stato) => (
                  <option key={stato.value} value={stato.value}>
                    {stato.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>

          {/* Prezzo negoziabile e preferenze contatto */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {/* Prezzo negoziabile */}
            <FormControl>
              <Checkbox
                isChecked={formData.prezzoNegoziabile}
                onChange={(e) =>
                  handleInputChange('prezzoNegoziabile', e.target.checked)
                }
                colorScheme="orange"
              >
                Prezzo negoziabile
              </Checkbox>
            </FormControl>

            {/* Preferenze contatto */}
            <FormControl>
              <FormLabel>Preferenze contatto</FormLabel>
              <Select
                value={formData.preferenzeContatto}
                onChange={(e) =>
                  handleInputChange('preferenzeContatto', e.target.value)
                }
                size="md"
              >
                {PREFERENZE_CONTATTO.map((pref) => (
                  <option key={pref.value} value={pref.value}>
                    {pref.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>

          {/* Galleria immagini */}
          <FormControl>
            <FormLabel>Immagini</FormLabel>
            <VStack spacing={4} align="stretch">
              {/* Preview immagini esistenti */}
              {formData.galleria.length > 0 && (
                <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                  {formData.galleria.map((img, index) => {
                    const imageUrl = getImageUrl(img);
                    const imageId =
                      img.id ||
                      img.documentId ||
                      img.attributes?.id ||
                      img.attributes?.documentId ||
                      `img-${index}`;
                    return (
                      <Box
                        key={imageId}
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        border="1px solid"
                        borderColor={borderColor}
                        bg={cardBg}
                      >
                        {imageUrl ? (
                          <Image
                            src={imageUrl}
                            alt={img.name || `Immagine ${index + 1}`}
                            objectFit="cover"
                            w="100%"
                            h="150px"
                          />
                        ) : (
                          <Box
                            w="100%"
                            h="150px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bg="gray.100"
                          >
                            <FaImage size={40} color="gray" />
                          </Box>
                        )}
                        <IconButton
                          icon={<FaTimes />}
                          size="sm"
                          position="absolute"
                          top={2}
                          right={2}
                          colorScheme="red"
                          onClick={() => handleRemoveImage(index)}
                          aria-label="Rimuovi immagine"
                        />
                      </Box>
                    );
                  })}
                </SimpleGrid>
              )}

              {/* Input file */}
              <Box>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  display="none"
                  id="file-upload"
                />
                <Button
                  as="label"
                  htmlFor="file-upload"
                  leftIcon={
                    uploadingImages ? <Spinner size="sm" /> : <FaUpload />
                  }
                  variant="outline"
                  size="md"
                  width="100%"
                  cursor="pointer"
                  isLoading={uploadingImages}
                  loadingText="Caricamento..."
                >
                  {uploadingImages ? 'Caricamento...' : 'Carica immagini'}
                </Button>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Puoi caricare massimo 5 immagini totali. Formati supportati:
                  JPG, PNG, GIF, WebP (max 5MB per immagine)
                  {formData.galleria.length > 0 && (
                    <Text as="span" fontWeight="medium" display="block" mt={1}>
                      Immagini caricate: {formData.galleria.length}/5
                    </Text>
                  )}
                </Text>
              </Box>
            </VStack>
          </FormControl>

          {/* Disclaimer privacy */}
          <Box
            bg={useColorModeValue('gray.50', 'gray.700')}
            borderRadius="md"
            p={4}
            border="1px solid"
            borderColor={useColorModeValue('gray.200', 'gray.600')}
          >
            <Text fontSize="sm" fontWeight="bold" mb={2}>
              Disclaimer privacy – Bazar CIL
            </Text>
            <Text
              fontSize="xs"
              color={useColorModeValue('gray.600', 'gray.400')}
              lineHeight="tall"
            >
              Inserendo un oggetto nel Bazar CIL, il tuo nome e cognome saranno
              visibili a tutti i soci dell&apos;associazione. Ti invitiamo a
              prestare attenzione alle informazioni che scegli di condividere
              nella descrizione o nei messaggi, evitando dati personali
              sensibili o dettagli non necessari.
            </Text>
            <Text
              fontSize="xs"
              color={useColorModeValue('gray.600', 'gray.400')}
              mt={2}
            >
              Il Bazar è uno spazio di comunità basato sulla fiducia e sul
              rispetto reciproco.
            </Text>
          </Box>

          {/* Bottoni */}
          <HStack spacing={4} justify="flex-end">
            <Button
              variant="outline"
              onClick={() => navigate('/bazar')}
              isDisabled={loading}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              colorScheme="brand"
              isLoading={loading}
              loadingText={bazar ? 'Aggiornamento...' : 'Creazione...'}
            >
              {bazar ? 'Aggiorna' : 'Crea'} oggetto
            </Button>
          </HStack>
        </VStack>
      </form>
    </Box>
  );
}
